'use client';

import type { IPagination, IUserProfile } from '@/api';
import { categoryService, tagService, userService } from '@/api';
import { toastService } from '@/components/common/Toaster';
import type { ReportType } from '@/features/user-detail/hooks/useUserReport';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useUser = () => {
	const queryClient = useQueryClient();

	const {
		data: profile,
		isLoading,
		error,
		refetch,
	} = useQuery({
		queryKey: ['user', 'profile'],
		queryFn: () => userService.getProfile(),
		refetchOnWindowFocus: false,
		staleTime: Number.POSITIVE_INFINITY,
	});

	const updateProfileMutation = useMutation({
		mutationFn: (data: IUserProfile) => userService.updateProfile(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
		},
	});

	return {
		profile: profile?.data,
		isLoading,
		error,
		isUpdating: updateProfileMutation.isPending,
		updateProfile: updateProfileMutation.mutate,
		refetchProfile: refetch,
	};
};

export const useFollowUser = () => {
	return useMutation({
		mutationFn: (userId: string) => userService.followUser(userId),
	});
};

export const useUnfollowUser = () => {
	return useMutation({
		mutationFn: (userId: string) => userService.unfollowUser(userId),
	});
};

export const useUserConversations = (userId: string, params: IPagination) => {
	return useQuery({
		queryKey: ['user-conversations'],
		queryFn: () => userService.getUserConversations(userId, params),
	});
};

export const useUserProfile = (userId: string) => {
	return useQuery({
		queryKey: ['user', userId],
		queryFn: () => userService.getUserProfile(userId),
	});
};

export const useBlockUser = (userId: string) => {
	const queryClient = useQueryClient();

	const blockMutation = useMutation({
		mutationFn: () => userService.blockUser(userId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
			queryClient.refetchQueries({ queryKey: ['user', userId], exact: true });
		},
		onError: (_) => {
			toastService.error('Failed to block user');
		},
	});

	const unblockMutation = useMutation({
		mutationFn: () => userService.unblockUser(userId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
			queryClient.refetchQueries({ queryKey: ['user', userId], exact: true });
		},
		onError: (_) => {
			toastService.error('Failed to unblock user');
		},
	});

	return {
		block: blockMutation.mutateAsync,
		unblock: unblockMutation.mutateAsync,
		isBlocking: blockMutation.isPending,
		isUnblocking: unblockMutation.isPending,
		blockError: blockMutation.error,
		unblockError: unblockMutation.error,
	};
};

export const useReportUser = (itemId: string, type: ReportType = 'user') => {
	const reportMutation = useMutation({
		mutationFn: (reasons: string[]) => {
			if (type === 'user') {
				return userService.reportUser(itemId, reasons);
			}
			if (type === 'category') {
				return categoryService.reportCategory(itemId);
			}
			return tagService.reportTag(itemId, reasons);
		},
	});

	return {
		report: reportMutation.mutateAsync,
		isReporting: reportMutation.isPending,
		reportError: reportMutation.error,
	};
};

export const useDeleteUser = () => {
	const deleteMutation = useMutation({
		mutationFn: () => userService.deleteUser(),
	});

	return {
		deleteUser: deleteMutation.mutateAsync,
		isDeletingUser: deleteMutation.isPending,
		deleteUserError: deleteMutation.error,
	};
};

export const useFollowUpQuestions = () => {
	return useQuery({
		queryKey: ['user', 'follow-up-questions'],
		queryFn: () => userService.getFollowUpQuestions({ page: 0, size: 100 }),
	});
};

export const useUserFollowing = (userId: string, params: IPagination) => {
	return useQuery({
		queryKey: ['user-following', userId],
		queryFn: () => userService.getFollowing(userId, params),
	});
};

export const useUserFollower = (userId: string, params: IPagination) => {
	return useQuery({
		queryKey: ['user-follower', userId],
		queryFn: () => userService.getFollowers(userId, params),
	});
};
